import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { SITE } from "@/config/site";
import { getPageSeo, type PageSeo } from "@/lib/api/seo.functions";
import { mergeSeo } from "@/lib/seo-head";

export const Route = createFileRoute("/contact")({
  loader: () => getPageSeo({ data: { path: "/contact" } }),
  head: ({ loaderData }) => mergeSeo({
    meta: [
      { name: "robots", content: "index, follow" },
      { title: "Contact Us · Surer Adda With Sanchayeeta — Booking & Enquiries" },
      { name: "description", content: "Contact Surer Adda With Sanchayeeta for live singing bookings, event performances, collaborations and general enquiries. Email, phone & WhatsApp." },
      { name: "keywords", content: "Contact Surer Adda With Sanchayeeta, Sanchayeeta Seal contact, singer booking contact, live singer enquiry, event singer phone, WhatsApp singer booking" },
      { name: "author", content: "Sanchayeeta Seal" },
      { property: "og:title", content: "Contact Us · Surer Adda With Sanchayeeta" },
      { property: "og:description", content: "Get in touch for live singing performance bookings, weddings, cultural programs, collaborations and general enquiries." },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "Surer Adda With Sanchayeeta" },
      { property: "og:url", content: `${SITE.url}/contact` },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Contact Us · Surer Adda With Sanchayeeta" },
      { name: "twitter:description", content: "Email, phone or WhatsApp Sanchayeeta Seal for booking enquiries and collaborations." },
    ],
    links: [{ rel: "canonical", href: `${SITE.url}/contact` }],
    scripts: [{
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "ContactPage",
        name: "Contact Surer Adda With Sanchayeeta",
        url: `${SITE.url}/contact`,
        mainEntity: {
          "@type": "Organization",
          name: "Surer Adda With Sanchayeeta",
          email: SITE.contact.email,
          telephone: SITE.contact.phone,
          url: SITE.url,
        },
      }),
    }],
  }, loaderData as PageSeo | null),
  component: ContactPage,
});

function ContactPage() {
  const waHref = `https://wa.me/${SITE.contact.whatsapp}`;
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main className="mx-auto max-w-3xl px-4 py-16 sm:px-6">
        <p className="font-script text-4xl text-primary">Contact Us</p>
        <h1 className="mt-2 text-3xl font-semibold">Get in touch with Surer Adda With Sanchayeeta</h1>

        <section className="prose prose-neutral mt-8 max-w-none text-foreground text-sm leading-relaxed">
          <p>Thank you for visiting Surer Adda With Sanchayeeta.</p>
          <p>
            We are always happy to connect with music lovers, event organizers, viewers, followers,
            and anyone interested in live musical performances, collaborations, or booking enquiries.
            If you would like to get in touch with us for event bookings, performance details,
            website-related queries, or general communication, please feel free to contact us using
            the information below.
          </p>

          <h2 className="mt-8 text-xl font-semibold">Get in Touch</h2>
          <ul className="mt-3 list-none space-y-2 pl-0">
            <li><strong>Website Name:</strong> Surer Adda With Sanchayeeta</li>
            <li><strong>Contact Person:</strong> Sanchayeeta</li>
            <li>
              <strong>Email:</strong>{" "}
              <a className="text-primary hover:underline" href={`mailto:${SITE.contact.email}`}>{SITE.contact.email}</a>
            </li>
            <li>
              <strong>Phone / WhatsApp:</strong>{" "}
              <a className="text-primary hover:underline" href={`tel:${SITE.contact.phone}`}>{SITE.contact.phone}</a>{" "}
              (<a className="text-primary hover:underline" href={waHref} target="_blank" rel="noopener noreferrer">WhatsApp</a>)
            </li>
            <li>
              <strong>Website:</strong>{" "}
              <a className="text-primary hover:underline" href={SITE.url} target="_blank" rel="noopener noreferrer">www.sureraddawithsanchayeeta.in</a>
            </li>
          </ul>

          <h2 className="mt-8 text-xl font-semibold">Enquiry Types</h2>
          <p>You may contact us for:</p>
          <ul className="mt-3 list-disc space-y-1 pl-5">
            <li>Live singing performance bookings</li>
            <li>Wedding, reception, birthday, and private event enquiries</li>
            <li>Cultural programs and stage show bookings</li>
            <li>Collaboration opportunities</li>
            <li>Questions related to our website content, music posts, or performances</li>
            <li>General feedback and support</li>
          </ul>

          <h2 className="mt-8 text-xl font-semibold">Response Time</h2>
          <p>
            We try our best to respond to all genuine enquiries as quickly as possible. However,
            response time may vary depending on performance schedules, workload, and the nature of
            the enquiry. We appreciate your patience and understanding.
          </p>

          <h2 className="mt-8 text-xl font-semibold">Important Note</h2>
          <p>When contacting us for event bookings, please mention relevant details such as:</p>
          <ul className="mt-3 list-disc space-y-1 pl-5">
            <li>Event date</li>
            <li>Event location</li>
            <li>Type of event</li>
            <li>Preferred performance requirement</li>
            <li>Contact details</li>
          </ul>
          <p>This helps us respond more efficiently and provide the right information.</p>

          <p className="mt-6">
            Thank you for your interest in Surer Adda With Sanchayeeta. We value your support and
            look forward to connecting with you.
          </p>

          <p className="mt-6 text-sm">
            Prefer a form? <Link to="/enquiry" className="text-primary hover:underline">Send an enquiry</Link>{" "}
            or <Link to="/book" className="text-primary hover:underline">book a performance</Link> online.
          </p>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
